import { FileUp } from "lucide-react";

import { ButtonLink } from "@/components/button-link";
import {
  ChallengeShell,
  StepsStrip,
} from "@/components/challenge/challenge-shell";
import { StartupJsonCard } from "@/legacy/startup-json-card";
import { Button } from "@/components/ui/button";
import type { Challenge } from "@/lib/api";
import { isDeadlinePassed } from "@/lib/format";

/**
 * A challenge the student simulates on their own machine.
 *
 * Kept as its own page rather than as branches inside one: nothing in this workflow -- the
 * `startup.json` that has to stay byte-matched to a Drive download, the local PyBullet install,
 * the encrypted `.npy` upload -- has a counterpart in the server-simulated one, and the
 * conditionals were already five deep before either page grew.
 *
 * Expected to be retired once every challenge runs on the server, but not soon: five archived
 * challenges and their 794 recordings are reached through here.
 */
export function LegacyChallengePage({ challenge }: { challenge: Challenge }) {
  const closed = isDeadlinePassed(challenge.deadline) || !challenge.active;

  return (
    <ChallengeShell
      challenge={challenge}
      action={
        closed ? (
          <Button disabled>Closed</Button>
        ) : (
          <ButtonLink to={`/challenges/${challenge.id}/submit`}>
            <FileUp className="size-4" aria-hidden />
            Submit a run
          </ButtonLink>
        )
      }
      setupSummary="Runs on your machine — update startup.json, play, then upload the recording."
      setup={
        <div className="space-y-4">
          <StepsStrip
            steps={[
              <>
                Update <code className="font-mono">startup.json</code> (below)
              </>,
              <>
                Extract the dataset to{" "}
                <code className="font-mono">data/exploration_data</code>
              </>,
              <>Run your player</>,
              <>
                Upload the <code className="font-mono">.npy</code> from{" "}
                <code className="font-mono">data/save/</code>
                {challenge.final_submission_link ? (
                  <>
                    , then{" "}
                    <span className="text-foreground font-medium">
                      submit via the official form
                    </span>
                  </>
                ) : null}
              </>,
            ]}
          />
          <StartupJsonCard challenge={challenge} />
        </div>
      }
    />
  );
}
